import {IGraphQLSubscriptionClient} from '@stores/gqlModel/IGraphQLSubscriptionClient';

export type GraphQLWsClientFactory = () => Promise<IGraphQLSubscriptionClient>;

export interface GraphQLClientConfig {
  key: string;
  httpEndpoint: string;
  wsClientFactory?: GraphQLWsClientFactory;
}

export interface DataClientServiceOptions {
  defaultKey: string;
  clients: GraphQLClientConfig[];
  tableClientMap?: Record<string, string>;
}

export interface IDataClientService {
  getClientByKey(key: string): GraphQLClientConfig;
  getDefaultClient(): GraphQLClientConfig;
  getClientForTable(table?: string): GraphQLClientConfig;
  getHttpEndpointByKey(key: string): string;
  getHttpEndpointForTable(table?: string): string;
  getWsClientByKey(key: string): Promise<IGraphQLSubscriptionClient | undefined>;
  getWsClientForTable(table?: string): Promise<IGraphQLSubscriptionClient | undefined>;
  assignTableClient(table: string, key: string): void;
  listClients(): GraphQLClientConfig[];
}

export class DataClientService implements IDataClientService {
  private readonly clients: Map<string, GraphQLClientConfig> = new Map();
  private readonly tableClientMap: Map<string, string> = new Map();
  private readonly defaultKey: string;
  private readonly wsClientPromises: Map<string, Promise<IGraphQLSubscriptionClient | undefined>> = new Map();

  constructor(options: DataClientServiceOptions) {
    if (!options.clients.length) {
      throw new Error('DataClientService requires at least one client configuration');
    }

    options.clients.forEach((client) => {
      this.clients.set(client.key, client);
    });

    if (!this.clients.has(options.defaultKey)) {
      throw new Error(`Default client key "${options.defaultKey}" is not defined in client list`);
    }

    this.defaultKey = options.defaultKey;

    if (options.tableClientMap) {
      Object.entries(options.tableClientMap).forEach(([table, key]) => {
        if (this.clients.has(key)) {
          this.tableClientMap.set(table, key);
        }
      });
    }
  }

  getClientByKey(key: string): GraphQLClientConfig {
    const client = this.clients.get(key);
    if (!client) {
      throw new Error(`GraphQL client with key "${key}" is not configured`);
    }
    return client;
  }

  getDefaultClient(): GraphQLClientConfig {
    return this.getClientByKey(this.defaultKey);
  }

  getClientForTable(table?: string): GraphQLClientConfig {
    if (table) {
      const mappedKey = this.tableClientMap.get(table);
      if (mappedKey && this.clients.has(mappedKey)) {
        return this.getClientByKey(mappedKey);
      }
    }
    return this.getDefaultClient();
  }

  getHttpEndpointByKey(key: string): string {
    return this.getClientByKey(key).httpEndpoint;
  }

  getHttpEndpointForTable(table?: string): string {
    return this.getClientForTable(table).httpEndpoint;
  }

  async getWsClientByKey(key: string): Promise<IGraphQLSubscriptionClient | undefined> {
    if (!this.clients.has(key)) {
      throw new Error(`GraphQL client with key "${key}" is not configured`);
    }

    if (!this.wsClientPromises.has(key)) {
      const config = this.clients.get(key)!;
      if (!config.wsClientFactory) {
        this.wsClientPromises.set(key, Promise.resolve(undefined));
      } else {
        const promise = (async () => {
          const client = await config.wsClientFactory!();
          return client;
        })();
        this.wsClientPromises.set(key, promise);
      }
    }

    return this.wsClientPromises.get(key)!;
  }

  getWsClientForTable(table?: string): Promise<IGraphQLSubscriptionClient | undefined> {
    const clientConfig = this.getClientForTable(table);
    return this.getWsClientByKey(clientConfig.key);
  }

  assignTableClient(table: string, key: string): void {
    if (!this.clients.has(key)) {
      throw new Error(`Cannot assign table "${table}" to unknown client key "${key}"`);
    }
    this.tableClientMap.set(table, key);
  }

  listClients(): GraphQLClientConfig[] {
    return Array.from(this.clients.values());
  }
}
